import { forwardRef, useCallback, useEffect, useState } from "react";
import type { ForwardedRef, MouseEventHandler, ReactNode } from "react";
import { Button } from "../../atoms/Button";
import { XIcon } from "../../lib/icons";
import { cn } from "../../lib/cn";
import {
  DEFAULT_SIDE_PANEL_WIDTH,
  useResizablePanel,
} from "./useResizablePanel";

/** Slide transition duration — also gates the unmount timer on close. */
const PANEL_TRANSITION_MS = 300;

export interface SidePanelHeaderProps {
  /** Panel heading. */
  title: ReactNode;
  /** Optional header actions (menus, links) beside the title. */
  actions?: ReactNode;
  /** Close callback for the ghost close button. */
  onClose: () => void;
  /** Close button test id — defaults to `${testId}-close` when SidePanel drives the header. */
  closeTestId?: string;
  className?: string;
}

/** Title bar of a SidePanel — title + optional actions + close ghost icon button. */
export const SidePanelHeader = forwardRef<HTMLDivElement, SidePanelHeaderProps>(
  ({ title, actions, onClose, closeTestId, className }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          "flex shrink-0 items-center justify-between gap-2 border-b border-border-default px-4 py-2",
          className,
        )}
      >
        <div className="flex min-w-0 items-center gap-2">
          <h2 className="type-h6 m-0 min-w-0 truncate">{title}</h2>
          {actions ? (
            <div className="flex shrink-0 items-center gap-1">{actions}</div>
          ) : null}
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="size-8 shrink-0"
          aria-label="Close panel"
          onClick={onClose}
          data-testid={closeTestId}
        >
          <XIcon className="size-4" aria-hidden="true" />
        </Button>
      </div>
    );
  },
);
SidePanelHeader.displayName = "SidePanelHeader";

export interface SidePanelProps {
  /** Whether the panel is open — mount/slide in, unmount/slide out. */
  open: boolean;
  onClose: () => void;
  /** Heading — the header (with close button) renders only when present (with `actions`). */
  title?: ReactNode;
  /** Header action nodes (rendered only when `title` is present). */
  actions?: ReactNode;
  children: ReactNode;
  /** Sticky footer strip under the scrollable body. */
  footer?: ReactNode;
  /** Show the left-edge drag handle (lg+ only — full-screen panels never resize). */
  resizable?: boolean;
  /** Controlled width in px — omit for uncontrolled with `defaultWidth`. */
  width?: number;
  /** Initial uncontrolled width (default 480). */
  defaultWidth?: number;
  /** Fires on drag end (and whenever the width commits). */
  onWidthChange?: (width: number) => void;
  testId?: string;
  /** Close button test id — defaults to `${testId}-close`. */
  closeTestId?: string;
  className?: string;
}

function usePanelMounted(open: boolean) {
  const [mounted, setMounted] = useState(open);
  useEffect(() => {
    if (open) {
      setMounted(true);
      return;
    }
    const timer = window.setTimeout(() => {
      setMounted(false);
    }, PANEL_TRANSITION_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [open]);
  return mounted;
}

function useEscapeClose(open: boolean, onClose: () => void) {
  useEffect(() => {
    if (!open) {
      return;
    }
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => {
      window.removeEventListener("keydown", handleKey);
    };
  }, [open, onClose]);
}

function PanelResizeHandle({
  dragging,
  onMouseDown,
}: {
  dragging: boolean;
  onMouseDown: MouseEventHandler;
}) {
  return (
    <div
      onMouseDown={onMouseDown}
      aria-hidden="true"
      className={cn(
        "absolute inset-y-0 left-0 z-(--z-panel-handle) hidden w-1.5 -translate-x-1/2 cursor-col-resize touch-none transition-colors duration-150 lg:block",
        dragging
          ? "bg-surface-primary"
          : "bg-transparent hover:bg-surface-primary-soft",
      )}
    />
  );
}

interface SidePanelViewProps extends SidePanelProps {
  panelRef: ForwardedRef<HTMLElement>;
  mounted: boolean;
  isDragging: boolean;
  beginDrag: MouseEventHandler;
}

function SidePanelView(props: SidePanelViewProps) {
  if (!props.mounted) {
    return null;
  }
  return (
    <aside
      ref={props.panelRef}
      data-testid={props.testId}
      data-state={props.open ? "open" : "closed"}
      data-resizing={props.isDragging || undefined}
      inert={!props.open}
      aria-hidden={!props.open || undefined}
      aria-label={typeof props.title === "string" ? props.title : undefined}
      className={cn(
        "fixed inset-0 z-(--z-panel) flex flex-col bg-surface-default transition-[transform,visibility] duration-300 ease-out",
        "lg:bottom-0 lg:left-auto lg:top-(--shell-topbar-height) lg:border-l lg:border-shell-border lg:shadow-panel lg:[width:var(--ds-side-panel-width,480px)]",
        props.open ? "visible translate-x-0" : "invisible translate-x-full",
        props.className,
      )}
    >
      {props.resizable !== false ? (
        <PanelResizeHandle
          dragging={props.isDragging}
          onMouseDown={props.beginDrag}
        />
      ) : null}
      {props.title ? (
        <SidePanelHeader
          title={props.title}
          actions={props.actions}
          onClose={props.onClose}
          closeTestId={
            props.closeTestId ??
            (props.testId ? `${props.testId}-close` : undefined)
          }
        />
      ) : null}
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        {props.children}
      </div>
      {props.footer ? (
        <div className="shrink-0 border-t border-border-default px-4 py-3">
          {props.footer}
        </div>
      ) : null}
    </aside>
  );
}

/**
 * Docked detail panel — port of the portal's SidePanel mechanics without
 * zustand. Fixed right below the top bar (top: var(--shell-topbar-height)),
 * height calc(100dvh - topbar), width driven by the --ds-side-panel-width
 * CSS variable so AppShell can compensate its content margin. Drag the
 * left-edge handle: rAF-throttled var updates, min var(--panel-min-width),
 * max 70vw, ESC closes. Below lg the panel is full-screen and not
 * resizable. Width state is internal by default — lift it via
 * `width`/`onWidthChange` if the host persists it.
 */
export const SidePanel = forwardRef<HTMLElement, SidePanelProps>(
  (props, ref) => {
    const [internalWidth, setInternalWidth] = useState(
      props.defaultWidth ?? DEFAULT_SIDE_PANEL_WIDTH,
    );
    const width = props.width ?? internalWidth;

    const commitWidth = useCallback(
      (next: number) => {
        if (props.width === undefined) {
          setInternalWidth(next);
        }
        props.onWidthChange?.(next);
      },
      [props.width, props.onWidthChange],
    );

    const { isDragging, beginDrag } = useResizablePanel({
      width,
      onWidthCommit: commitWidth,
    });

    const mounted = usePanelMounted(props.open);
    useEscapeClose(props.open, props.onClose);
    return (
      <SidePanelView
        {...props}
        panelRef={ref}
        mounted={mounted}
        isDragging={isDragging}
        beginDrag={beginDrag}
      />
    );
  },
);
SidePanel.displayName = "SidePanel";
